import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import WorkspaceRail from "@/components/WorkspaceRail";
import { Inbox, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const ALL_CHATS_POLL_MS = 7000;

const WS_PALETTE = [
  "#D96C4A",
  "#3A5A40",
  "#5B4C7A",
  "#2C5F77",
  "#7A4A2C",
  "#8C3A1F",
  "#4B6C3A",
  "#7A3D5D",
];

function pillBg(name) {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return WS_PALETTE[h % WS_PALETTE.length];
}

function workspaceInitials(name) {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function AllChats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(
    async ({ silent } = { silent: false }) => {
      try {
        const { data } = await api.get("/me/chats");
        setChats(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!silent) toast.error(formatApiError(e));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (user && user.id) {
      setLoading(true);
      fetchAll();
    }
  }, [user, fetchAll]);

  useEffect(() => {
    if (!user || !user.id) return undefined;
    const id = setInterval(
      () => fetchAll({ silent: true }),
      ALL_CHATS_POLL_MS
    );
    return () => clearInterval(id);
  }, [user, fetchAll]);

  return (
    <div
      className="flex h-screen w-full bg-[#FAF9F7] overflow-hidden"
      data-testid="all-chats-page"
    >
      <WorkspaceRail activeWorkspaceId={null} />
      <main className="flex-1 flex flex-col bg-white overflow-hidden">
        <header className="px-8 md:px-14 pt-10 pb-7 border-b border-[#DCD7CE] bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Inbox
              size={16}
              className="text-[#D96C4A]"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
              All your conversations
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2C2A29] leading-none">
            Everything, in one place.
          </h1>
          <p className="font-sans text-sm text-[#7A7571] mt-3 max-w-xl leading-relaxed">
            The freshest threads across every workspace you're in. Open one to
            jump straight into it.
          </p>
        </header>

        <div
          className="flex-1 overflow-y-auto px-8 md:px-14 py-7"
          data-testid="all-chats-scroll"
        >
          {loading ? (
            <div className="flex items-center gap-2 font-sans text-sm text-[#7A7571] py-10">
              <Loader2 className="animate-spin" size={14} /> Loading…
            </div>
          ) : chats.length === 0 ? (
            <div
              className="py-16 text-center max-w-md mx-auto"
              data-testid="all-chats-empty"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#F3F1EC] border border-[#DCD7CE] flex items-center justify-center">
                  <MessageCircle
                    size={22}
                    className="text-[#D96C4A]"
                    strokeWidth={1.6}
                  />
                </div>
              </div>
              <h2 className="font-serif text-3xl text-[#2C2A29] mb-2">
                Nothing here yet.
              </h2>
              <p className="font-sans text-sm text-[#7A7571]">
                Open a workspace from the rail on the left to start a
                conversation.
              </p>
            </div>
          ) : (
            <ul
              className="max-w-3xl space-y-1.5"
              data-testid="all-chats-list"
            >
              {chats.map((c) => {
                const wsName = c.workspace?.name || "Workspace";
                const wsColor = pillBg(wsName);
                return (
                  <li
                    key={c.chat_id}
                    data-testid={`all-chats-row-${c.chat_id}`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/w/${c.workspace.slug}/c/${c.chat_id}`)
                      }
                      className="group w-full text-left px-5 py-4 rounded-xl border border-[#EFEAE0] bg-white hover:bg-[#F8F6F1] hover:border-[#DCD7CE] transition-all flex items-start gap-4"
                      data-testid={`all-chats-button-${c.chat_id}`}
                    >
                      <div
                        className="w-11 h-11 rounded-lg flex items-center justify-center font-sans font-semibold text-sm text-white shrink-0 shadow-sm"
                        style={{ background: wsColor }}
                        aria-hidden="true"
                      >
                        {workspaceInitials(wsName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-1">
                          <h3 className="font-serif text-xl text-[#2C2A29] truncate flex-1 leading-tight">
                            {c.title || "Untitled"}
                          </h3>
                          <span className="font-sans text-[10px] uppercase tracking-widest text-[#A5A29E] shrink-0">
                            {formatRelative(c.updated_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-md border"
                            style={{
                              background: `${wsColor}1F`,
                              color: wsColor,
                              borderColor: `${wsColor}44`,
                            }}
                            data-testid={`all-chats-ws-pill-${c.chat_id}`}
                          >
                            {wsName}
                          </span>
                          <p className="font-sans text-sm text-[#7A7571] truncate">
                            {c.last_message_preview ||
                              `Started by ${c.created_by_name || "—"}`}
                          </p>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
